// src/components/ui/sections/PricingAndDurationSection/PricingAndDurationSection.jsx
import React, { useState, useEffect } from 'react';
import {
  Plus,
  DollarSign,
  Clock,
  Calculator,
  Package
} from 'lucide-react';

// shadcn components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../card';
import { Input } from '../../input';
import { Label } from '../../label';
import { Textarea } from '../../textarea';
import { Button } from '../../button';

import { cn } from '../../../../utils/cn';
import ComponentListItem from '../../cards/ComponentListItem';
import ComponentForm from '../../forms/ComponentForm';

const PricingAndDurationSection = ({
  service,
  components = [],
  onUpdateService,
  onCreateComponent,
  onUpdateComponent,
  onDeleteComponent,
  saving = false
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);

  // Local form state for base pricing
  const [basePriceData, setBasePriceData] = useState({
    base_price: '',
    base_duration: '',
    description: ''
  });

  const [lastSavedBasePricing, setLastSavedBasePricing] = useState({});

  // Initialize base pricing from service
  useEffect(() => {
    if (service) {
      const newData = {
        base_price: service.base_price || '',
        base_duration: service.base_duration || '',
        description: service.description || ''
      };
      setBasePriceData(newData);
      setLastSavedBasePricing(newData);
    }
  }, [service]);

  // Auto-save base pricing on blur
  const handleBasePricingBlur = async (field) => {
    if (basePriceData[field] !== lastSavedBasePricing[field]) {
      await handleSaveBasePricing();
    }
  };

  const handleSaveBasePricing = async () => {
    const updateData = {
      base_price: basePriceData.base_price ? parseFloat(basePriceData.base_price) : null,
      base_duration: basePriceData.base_duration ? parseInt(basePriceData.base_duration) : null,
      description: basePriceData.description?.trim() || null
    };

    await onUpdateService(service.id, updateData);
    setLastSavedBasePricing(basePriceData);
  };

  // Handle component operations
  const handleCreateComponent = (componentData) => {
    onCreateComponent(service.id, componentData);
    setShowCreateForm(false);
  };

  const handleUpdateComponent = (componentId, updateData) => {
    onUpdateComponent(service.id, componentId, updateData);
    setEditingComponent(null);
  };

  const handleDeleteComponent = (component) => {
    onDeleteComponent(service.id, component.id, component.name);
  };

  // Calculate totals
  const basePrice = parseFloat(basePriceData.base_price) || 0;
  const baseDuration = parseInt(basePriceData.base_duration) || 0;

  const addonPriceTotal = components.reduce((sum, c) => sum + (parseFloat(c.price) || 0), 0);
  const addonDurationTotal = components.reduce((sum, c) => sum + (parseInt(c.duration_minutes) || 0), 0);

  const totalPriceWithAddons = basePrice + addonPriceTotal;
  const totalDurationWithAddons = baseDuration + addonDurationTotal;

  return (
    <div className="space-y-6">
      {/* Base Service Pricing */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Base Service
          </CardTitle>
          <CardDescription>
            The starting price and duration for this service (always included)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Base Price */}
            <div className="space-y-2">
              <Label htmlFor="base_price" className="flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Base Price
              </Label>
              <Input
                id="base_price"
                type="number"
                step="0.01"
                min="0"
                value={basePriceData.base_price}
                onChange={(e) => setBasePriceData(prev => ({ ...prev, base_price: e.target.value }))}
                onBlur={() => handleBasePricingBlur('base_price')}
                placeholder="0.00"
              />
            </div>

            {/* Base Duration */}
            <div className="space-y-2">
              <Label htmlFor="base_duration" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Base Duration (minutes)
              </Label>
              <Input
                id="base_duration"
                type="number"
                min="1"
                value={basePriceData.base_duration}
                onChange={(e) => setBasePriceData(prev => ({ ...prev, base_duration: e.target.value }))}
                onBlur={() => handleBasePricingBlur('base_duration')}
                placeholder="60"
              />
            </div>
          </div>

          {/* What's Included */}
          <div className="space-y-2">
            <Label htmlFor="whats_included">
              What's Included (optional)
            </Label>
            <Textarea
              id="whats_included"
              value={basePriceData.description}
              onChange={(e) => setBasePriceData(prev => ({ ...prev, description: e.target.value }))}
              onBlur={() => handleBasePricingBlur('description')}
              placeholder="Describe what's included in the base service..."
              rows={2}
              className="resize-none"
            />
          </div>
        </CardContent>
      </Card>

      {/* Optional Add-ons */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Optional Add-ons
                {components.length > 0 && (
                  <span className="ml-2 px-2 py-1 bg-primary/20 text-primary text-sm rounded-lg font-normal">
                    {components.length}
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                Extras customers can add to customize their service
              </CardDescription>
            </div>

            <Button
              size="sm"
              onClick={() => setShowCreateForm(true)}
              disabled={saving}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Add-on
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {components.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-border rounded-xl">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <h4 className="text-foreground font-medium mb-2">
                No add-ons yet
              </h4>
              <p className="text-muted-foreground text-sm mb-4 max-w-sm mx-auto">
                Create optional add-ons that customers can select to customize their service
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateForm(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add First Add-on
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {components.map((component) => (
                <ComponentListItem
                  key={component.id}
                  component={component}
                  onEdit={(component) => setEditingComponent(component)}
                  onDelete={() => handleDeleteComponent(component)}
                  isEditing={editingComponent?.id === component.id}
                  onSave={(updateData) => handleUpdateComponent(component.id, updateData)}
                  onCancelEdit={() => setEditingComponent(null)}
                  saving={saving}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pricing Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Pricing Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Base Service */}
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <span className="text-muted-foreground">Base Service</span>
              <div className="text-right">
                <div className="font-medium">
                  ${basePrice.toFixed(2)}
                </div>
                <div className="text-sm text-muted-foreground">
                  {baseDuration} min
                </div>
              </div>
            </div>

            {/* Add-ons Total */}
            {components.length > 0 && (
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="text-muted-foreground">
                  All Add-ons ({components.length})
                </span>
                <div className="text-right">
                  <div className="font-medium">
                    +${addonPriceTotal.toFixed(2)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    +{addonDurationTotal} min
                  </div>
                </div>
              </div>
            )}

            {/* Total with All Add-ons */}
            <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg border border-primary/20">
              <span className="font-medium">
                {components.length > 0 ? 'Maximum Total' : 'Total'}
              </span>
              <div className="text-right">
                <div className="text-lg font-bold text-primary">
                  ${totalPriceWithAddons.toFixed(2)}
                </div>
                <div className="text-sm text-muted-foreground">
                  {totalDurationWithAddons} min ({(totalDurationWithAddons / 60).toFixed(1)} hrs)
                </div>
              </div>
            </div>

            {/* Price Range Display */}
            {components.length > 0 && (
              <div className="pt-3 border-t">
                <div className="text-sm text-muted-foreground text-center">
                  Price Range: <span className="font-medium text-foreground">${basePrice.toFixed(2)} - ${totalPriceWithAddons.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Component Form Modal */}
      {showCreateForm && (
        <ComponentForm
          onSave={handleCreateComponent}
          onCancel={() => setShowCreateForm(false)}
          saving={saving}
        />
      )}

      {/* Edit Component Form Modal */}
      {editingComponent && (
        <ComponentForm
          component={editingComponent}
          onSave={(data) => handleUpdateComponent(editingComponent.id, data)}
          onCancel={() => setEditingComponent(null)}
          saving={saving}
        />
      )}
    </div>
  );
};

export default PricingAndDurationSection;
